import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Application, Workflow, WorkflowStatus, Position } from '@/types'
import { apiService } from '@/services/api.service'

export const useKanbanStore = defineStore('kanban', () => {
  const applications = ref<Application[]>([])
  const workflows = ref<Workflow[]>([])
  const positions = ref<Position[]>([])
  const selectedWorkflow = ref<Workflow | null>(null)
  const selectedPosition = ref<Position | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function fetchPositions() {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.get<Position[]>('/positions')
      positions.value = response.data
      
      // Auto-select first position if available
      if (positions.value.length > 0 && !selectedPosition.value) {
        selectedPosition.value = positions.value[0]
        await fetchApplicationsForPosition(selectedPosition.value.id)
        
        // Fetch workflow if position has one
        if (selectedPosition.value.workflow_id) {
          await fetchWorkflow(selectedPosition.value.workflow_id)
        }
      }
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to fetch positions'
      throw error.value
    } finally {
      loading.value = false
    }
  }

  async function fetchApplicationsForPosition(positionId: string) {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.get<Application[]>(`/positions/${positionId}/applications`)
      applications.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to fetch applications'
      throw error.value
    } finally {
      loading.value = false
    }
  }

  async function fetchWorkflow(workflowId: string) {
    try {
      const response = await apiService.get<Workflow>(`/workflows/${workflowId}`)
      selectedWorkflow.value = response.data
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to fetch workflow'
      throw error.value
    }
  }

  async function moveApplication(applicationId: string, newStageId: string, notes?: string) {
    try {
      const response = await apiService.put(`/applications/${applicationId}/status`, {
        status_id: newStageId,
        notes: notes || null,
        source: 'kanban'
      })

      // Update local state with the full status object from the response
      const app = applications.value.find(a => a.id === applicationId)
      if (app && response.data) {
        // Backend should return updated application with current_status
        app.currentStageId = newStageId
        if (response.data.current_status) {
          app.current_status = response.data.current_status
        } else if (selectedWorkflow.value) {
          // Fallback: find the status from the workflow
          const status = selectedWorkflow.value.statuses?.find(s => s.id === newStageId)
          if (status) {
            app.current_status = {
              id: status.id,
              name: status.name,
              display_name: status.display_name
            }
          }
        }
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail
      if (typeof detail === 'object' && detail?.code === 'TRIAL_EXPIRED') {
        error.value = detail.message
      } else {
        error.value = (typeof detail === 'string' ? detail : null) || err.response?.data?.error || 'Failed to move application'
      }
      throw error.value
    }
  }

  function getApplicationsByStage(stageId: string): Application[] {
    return applications.value.filter(app => 
      app.current_status?.id === stageId || app.currentStageId === stageId
    )
  }

  async function selectPosition(positionId: string) {
    const position = positions.value.find(p => p.id === positionId)
    if (position) {
      selectedPosition.value = position
      await fetchApplicationsForPosition(positionId)
      
      // Fetch workflow if position has one
      if (position.workflow_id) {
        await fetchWorkflow(position.workflow_id)
      }
    }
  }

  return {
    applications,
    workflows,
    positions,
    selectedWorkflow,
    selectedPosition,
    loading,
    error,
    fetchPositions,
    fetchApplicationsForPosition,
    fetchWorkflow,
    moveApplication,
    getApplicationsByStage,
    selectPosition
  }
})
